import React, { useMemo, useState } from 'react';
import {
  FiDatabase as Database, FiAlertTriangle as AlertTriangle,
  FiZap as Zap, FiActivity as Activity, FiTrash2 as Trash2,
  FiChevronDown, FiChevronUp, FiAlertCircle, FiCheckCircle, FiInfo
} from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import { getUtilizationLevel, getUtilizationColorClasses } from '../../../../../hooks/useMemoryAlerts';
import {
  METRIC_EXPLANATIONS, HEALTH_BANNERS, RECOMMENDATIONS
} from './memoryTooltips';
import { BLUE, PURPLE, GREEN, GRAY, AMBER, RED } from '../../../../../constants/chartColors';

// Color constants
const MEMORY_TYPE_COLORS: Record<string, string> = {
  working: BLUE,
  conversation: PURPLE,
  tool_cache: GREEN,
  other: GRAY,
};

const OPERATION_COLORS: Record<string, string> = {
  read: GREEN,
  write: BLUE,
  update: AMBER,
  prune: RED,
  other: GRAY,
};

interface MemorySnapshot {
  run_id?: string;
  token_count?: number;
  max_tokens?: number;
  memory_type?: string;
  operation?: string;
  timestamp?: string;
}

interface MemoryStats {
  total_snapshots?: number;
  avg_tokens?: number;
  cache_hit_rate?: number;
  by_memory_type?: Record<string, number>;
  by_operation?: Record<string, number>;
}

interface MemoryFilters {
  memoryType: string;
  operation: string;
  runId: string;
  [key: string]: string;
}

interface Props {
  stats: MemoryStats | null | undefined;
  snapshots: MemorySnapshot[] | null | undefined;
  loading: boolean;
  filters: MemoryFilters;
  updateFilters: (updates: Partial<MemoryFilters>) => void;
  onNavigateToRun: (runId: string) => void;
}

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
);

type HealthStatus = 'healthy' | 'warning' | 'critical';

interface StatusDotProps {
  status: string;
}

const StatusDot: React.FC<StatusDotProps> = ({ status }) => {
  const colors: Record<string, string> = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500',
  };
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] ?? 'bg-gray-400'}`} />
  );
};

interface NarrativeMetricCardProps {
  title: string;
  value: string | number;
  status: string;
  explanation: string;
  recommendation: string;
  icon: React.ComponentType<{ className?: string }>;
  loading: boolean;
}

const NarrativeMetricCard: React.FC<NarrativeMetricCardProps> = ({ title, value, status, explanation, recommendation, icon: Icon, loading }) => {
  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5">
        <Skeleton className="h-4 w-24 mb-3" />
        <Skeleton className="h-8 w-20 mb-2" />
        <Skeleton className="h-3 w-full" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StatusDot status={status} />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        </div>
        <Icon className="w-5 h-5 text-gray-400" />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{explanation}</p>
      {recommendation && status !== 'healthy' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-start gap-1">
          <FiInfo className="w-3 h-3 mt-0.5 flex-shrink-0" />
          {recommendation}
        </p>
      )}
    </div>
  );
};

interface HealthBannerProps {
  status: string;
  loading: boolean;
}

const HealthBanner: React.FC<HealthBannerProps> = ({ status, loading }) => {
  if (loading) return null;

  const banner = HEALTH_BANNERS[status];
  if (!banner) return null;

  const icons: Record<string, React.ReactNode> = {
    critical: <FiAlertCircle className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    healthy: <FiCheckCircle className="w-5 h-5" />,
  };

  return (
    <div className={`flex items-center gap-3 px-5 py-3 rounded-lg border ${banner.color}`}>
      <span className={banner.textColor}>{icons[status]}</span>
      <p className={`text-sm font-medium ${banner.textColor}`}>{banner.message}</p>
    </div>
  );
};

interface ContextUtilizationGaugeProps {
  percentage: number;
  loading: boolean;
}

const ContextUtilizationGauge: React.FC<ContextUtilizationGaugeProps> = ({ percentage, loading }) => {
  const { level } = getUtilizationLevel(percentage, 100);
  const colors = getUtilizationColorClasses(level);

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  if (loading) {
    return (
      <div className="flex flex-col items-center">
        <Skeleton className="w-32 h-32 rounded-full" />
        <Skeleton className="h-4 w-24 mt-2" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="64" cy="64" r="45" stroke="currentColor" strokeWidth="10"
            fill="none" className="text-gray-200 dark:text-gray-700" />
          <circle cx="64" cy="64" r="45" stroke="currentColor" strokeWidth="10"
            fill="none" strokeLinecap="round" strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset} className={colors.text}
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-bold ${colors.text}`}>
            {percentage.toFixed(0)}%
          </span>
        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Avg. Context Usage</p>
    </div>
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value?: number }>;
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{label}</p>
      {payload.map((entry) => (
        <p key={`tooltip-${entry.name}`} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {entry.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

interface RecommendationsAccordionProps {
  items: string[];
}

const RecommendationsAccordion: React.FC<RecommendationsAccordionProps> = ({ items }) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <FiInfo className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {items.length} recommendation{items.length === 1 ? '' : 's'} for your agents
          </span>
        </div>
        {expanded
          ? <FiChevronUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          : <FiChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        }
      </button>
      {expanded && (
        <div className="px-5 pb-4 space-y-3">
          {items.map((item, i) => (
            <div key={`rec-${item.slice(0, 50)}-${i}`} className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">&#8226;</span>
              <p className="text-sm text-amber-700 dark:text-amber-300">{item}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface AgentAtRisk {
  run_id: string;
  utilization: number;
  token_count: number;
  max_tokens: number;
  memory_type: string | undefined;
  timestamp: string | undefined;
}

const MemoryOverviewTab: React.FC<Props> = ({ stats, snapshots, loading, onNavigateToRun }) => {
  const metrics = useMemo(() => {
    if (!stats) {
      return {
        totalSnapshots: 0, avgTokens: 0, avgUtilization: 0,
        cacheHitRate: 0, contextLimitEvents: 0, pruneOperations: 0,
        runsAtRisk: 0,
      };
    }

    const contextLimitEvents = snapshots?.filter(s =>
      s.token_count && s.max_tokens && s.token_count >= 0.9 * s.max_tokens
    ).length ?? 0;

    const snapshotsWithUtilization = snapshots?.filter(s => s.token_count && s.max_tokens) ?? [];
    const avgUtilization = snapshotsWithUtilization.length > 0
      ? snapshotsWithUtilization.reduce((sum, s) => sum + (s.token_count! / s.max_tokens!) * 100, 0) / snapshotsWithUtilization.length
      : 0;

    const runIds = new Set<string>();
    snapshots?.forEach(s => {
      if (s.run_id && s.token_count && s.max_tokens && (s.token_count / s.max_tokens) >= 0.7) {
        runIds.add(s.run_id);
      }
    });

    return {
      totalSnapshots: stats.total_snapshots ?? 0,
      avgTokens: stats.avg_tokens ?? 0,
      avgUtilization,
      cacheHitRate: stats.cache_hit_rate ?? 0,
      contextLimitEvents,
      pruneOperations: stats.by_operation?.prune ?? 0,
      runsAtRisk: runIds.size,
    };
  }, [stats, snapshots]);

  const healthStatus = useMemo((): HealthStatus => {
    if (metrics.contextLimitEvents > 0 || metrics.avgUtilization >= 90) return 'critical';
    if (metrics.runsAtRisk > 0 || metrics.pruneOperations > 10 || metrics.avgUtilization >= 70) return 'warning';
    return 'healthy';
  }, [metrics]);

  const cardStatuses = useMemo(() => {
    let contextStatus: string;
    if (metrics.avgUtilization >= 90) contextStatus = 'critical';
    else if (metrics.avgUtilization >= 70) contextStatus = 'warning';
    else contextStatus = 'healthy';

    let cacheStatus: string;
    if (metrics.cacheHitRate < 30) cacheStatus = 'critical';
    else if (metrics.cacheHitRate < 50) cacheStatus = 'warning';
    else cacheStatus = 'healthy';

    let pruningStatus: string;
    if (metrics.pruneOperations > 20) pruningStatus = 'critical';
    else if (metrics.pruneOperations > 10) pruningStatus = 'warning';
    else pruningStatus = 'healthy';

    let runsAtRiskStatus: string;
    if (metrics.runsAtRisk > 3) runsAtRiskStatus = 'critical';
    else if (metrics.runsAtRisk > 0) runsAtRiskStatus = 'warning';
    else runsAtRiskStatus = 'healthy';

    return {
      context: contextStatus,
      cache: cacheStatus,
      pruning: pruningStatus,
      runsAtRisk: runsAtRiskStatus,
    };
  }, [metrics]);

  const narrative = useMemo((): string[] => {
    if (!stats || metrics.totalSnapshots === 0) return [];

    const sentences: string[] = [];
    sentences.push(`${metrics.totalSnapshots.toLocaleString()} memory snapshots recorded in the selected period.`);

    if (stats.by_memory_type) {
      const sorted = Object.entries(stats.by_memory_type).sort((a, b) => b[1] - a[1]);
      if (sorted.length > 0) {
        const [topType, topCount] = sorted[0];
        const pct = ((topCount / metrics.totalSnapshots) * 100).toFixed(0);
        sentences.push(`${topType.replace('_', ' ')} is the most active memory type, accounting for ${pct}% of all snapshots.`);
      }
    }

    if (metrics.runsAtRisk > 0) {
      sentences.push(`${metrics.runsAtRisk} agent run${metrics.runsAtRisk === 1 ? ' is' : 's are'} approaching or exceeding context limits.`);
    } else {
      sentences.push('No agent runs are currently at risk of hitting context limits.');
    }

    if (metrics.pruneOperations > 0) {
      sentences.push(`${metrics.pruneOperations} prune operations occurred, which means agents needed to forget old information to make room.`);
    }

    if (stats.cache_hit_rate != null) {
      if (metrics.cacheHitRate >= 70) {
        sentences.push(`Cache hit rate is strong at ${metrics.cacheHitRate.toFixed(0)}%, meaning agents are efficiently reusing stored results.`);
      } else if (metrics.cacheHitRate >= 40) {
        sentences.push(`Cache hit rate is ${metrics.cacheHitRate.toFixed(0)}% — there may be room to improve tool result reuse.`);
      } else {
        sentences.push(`Cache hit rate is low at ${metrics.cacheHitRate.toFixed(0)}%. Agents are making many redundant calls.`);
      }
    }

    return sentences;
  }, [stats, metrics]);

  const recommendations = useMemo((): string[] => {
    const items: string[] = [];
    if (metrics.avgUtilization >= 70) items.push(RECOMMENDATIONS.high_utilization);
    if (metrics.pruneOperations > 10) items.push(RECOMMENDATIONS.many_prunes);
    if (metrics.cacheHitRate < 50 && stats?.cache_hit_rate != null) items.push(RECOMMENDATIONS.low_cache);
    if (metrics.runsAtRisk > 0) items.push(RECOMMENDATIONS.runs_at_risk);
    return items;
  }, [metrics, stats]);

  const memoryTypeData = useMemo(() => {
    if (!stats?.by_memory_type) return [];
    return Object.entries(stats.by_memory_type).map(([type, count]) => ({
      name: type.replace('_', ' '),
      value: count,
      fill: MEMORY_TYPE_COLORS[type] ?? MEMORY_TYPE_COLORS.other,
    }));
  }, [stats]);

  const operationsData = useMemo(() => {
    if (!stats?.by_operation) return [];
    return Object.entries(stats.by_operation).map(([operation, count]) => ({
      name: operation,
      count,
      fill: OPERATION_COLORS[operation] ?? OPERATION_COLORS.other,
    }));
  }, [stats]);

  interface DayAccum {
    working: number;
    conversation: number;
    tool_cache: number;
    count: number;
    [key: string]: number;
  }

  const tokenTrendData = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    const groupedByDay = snapshots.reduce<Record<string, DayAccum>>((acc, snapshot) => {
      const date = new Date(snapshot.timestamp ?? Date.now()).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { working: 0, conversation: 0, tool_cache: 0, count: 0 };
      }
      const type = snapshot.memory_type ?? 'other';
      if (acc[date][type] !== undefined) {
        acc[date][type] += snapshot.token_count ?? 0;
      }
      acc[date].count++;
      return acc;
    }, {});

    return Object.entries(groupedByDay)
      .map(([date, data]) => ({
        date,
        working: Math.round(data.working / data.count),
        conversation: Math.round(data.conversation / data.count),
        tool_cache: Math.round(data.tool_cache / data.count),
      }))
      .slice(-7);
  }, [snapshots]);

  const agentsAtRisk = useMemo((): AgentAtRisk[] => {
    if (!snapshots || snapshots.length === 0) return [];
    const runUtilization: Record<string, AgentAtRisk> = {};
    snapshots.forEach(s => {
      if (!s.run_id || !s.token_count || !s.max_tokens) return;
      const utilization = (s.token_count / s.max_tokens) * 100;
      if (!runUtilization[s.run_id] || utilization > runUtilization[s.run_id].utilization) {
        runUtilization[s.run_id] = {
          run_id: s.run_id, utilization,
          token_count: s.token_count, max_tokens: s.max_tokens,
          memory_type: s.memory_type, timestamp: s.timestamp,
        };
      }
    });
    return Object.values(runUtilization)
      .filter(r => r.utilization >= 70)
      .sort((a, b) => b.utilization - a.utilization)
      .slice(0, 5);
  }, [snapshots]);

  const formatNumber = (num: number | null | undefined): string => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Health Banner */}
      <HealthBanner status={healthStatus} loading={loading} />

      {/* Narrative Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <NarrativeMetricCard
          title={METRIC_EXPLANATIONS.context_usage.title}
          value={`${metrics.avgUtilization.toFixed(0)}%`}
          status={cardStatuses.context}
          explanation={METRIC_EXPLANATIONS.context_usage[cardStatuses.context as keyof typeof METRIC_EXPLANATIONS.context_usage]}
          recommendation={RECOMMENDATIONS.high_utilization}
          icon={Activity}
          loading={loading}
        />
        <NarrativeMetricCard
          title={METRIC_EXPLANATIONS.cache_efficiency.title}
          value={`${metrics.cacheHitRate.toFixed(0)}%`}
          status={cardStatuses.cache}
          explanation={METRIC_EXPLANATIONS.cache_efficiency[cardStatuses.cache as keyof typeof METRIC_EXPLANATIONS.cache_efficiency]}
          recommendation={RECOMMENDATIONS.low_cache}
          icon={Zap}
          loading={loading}
        />
        <NarrativeMetricCard
          title={METRIC_EXPLANATIONS.pruning_activity.title}
          value={metrics.pruneOperations}
          status={cardStatuses.pruning}
          explanation={METRIC_EXPLANATIONS.pruning_activity[cardStatuses.pruning as keyof typeof METRIC_EXPLANATIONS.pruning_activity]}
          recommendation={RECOMMENDATIONS.many_prunes}
          icon={Trash2}
          loading={loading}
        />
        <NarrativeMetricCard
          title={METRIC_EXPLANATIONS.runs_at_risk.title}
          value={metrics.runsAtRisk}
          status={cardStatuses.runsAtRisk}
          explanation={METRIC_EXPLANATIONS.runs_at_risk[cardStatuses.runsAtRisk as keyof typeof METRIC_EXPLANATIONS.runs_at_risk]}
          recommendation={RECOMMENDATIONS.runs_at_risk}
          icon={AlertTriangle}
          loading={loading}
        />
      </div>

      {/* What's Happening Section */}
      {!loading && narrative.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <FiInfo className="w-4 h-4 text-gray-500" />
            What&apos;s happening
          </h3>
          <div className="space-y-1.5">
            {narrative.map((sentence, i) => (
              <p key={`narr-${sentence.slice(0, 30)}-${i}`} className="text-sm text-gray-600 dark:text-gray-400">{sentence}</p>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      <RecommendationsAccordion items={recommendations} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Context Utilization Gauge */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Context Utilization
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            How full your agents&apos; context windows are on average
          </p>
          <div className="flex justify-center">
            <ContextUtilizationGauge percentage={metrics.avgUtilization} loading={loading} />
          </div>
        </div>

        {/* Memory Type Distribution */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 text-gray-500 dark:text-gray-400">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Memory Type Distribution
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Breakdown of which memory types your agents use most
          </p>
          {loading && <Skeleton className="h-48 w-full" />}
          {!loading && memoryTypeData.length > 0 && (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={memoryTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={70}
                  label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {memoryTypeData.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {!loading && memoryTypeData.length === 0 && (
            <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>

        {/* Operations Breakdown */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 text-gray-500 dark:text-gray-400">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Operations Breakdown
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            What your agents are doing with their memory: reads, writes, updates, and prunes
          </p>
          {loading && <Skeleton className="h-48 w-full" />}
          {!loading && operationsData.length > 0 && (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={operationsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis type="number" tick={{ fill: 'currentColor', fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: 'currentColor', fontSize: 12 }} width={60} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {operationsData.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {!loading && operationsData.length === 0 && (
            <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Token Usage Trend */}
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 text-gray-500 dark:text-gray-400">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Token Usage Trend
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Average daily token usage by memory type over the past week
        </p>
        {loading && <Skeleton className="h-64 w-full" />}
        {!loading && tokenTrendData.length > 0 && (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={tokenTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fill: 'currentColor', fontSize: 12 }} />
              <YAxis tick={{ fill: 'currentColor', fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="working" stackId="1"
                stroke={MEMORY_TYPE_COLORS.working} fill={MEMORY_TYPE_COLORS.working}
                fillOpacity={0.6} name="Working" />
              <Area type="monotone" dataKey="conversation" stackId="1"
                stroke={MEMORY_TYPE_COLORS.conversation} fill={MEMORY_TYPE_COLORS.conversation}
                fillOpacity={0.6} name="Conversation" />
              <Area type="monotone" dataKey="tool_cache" stackId="1"
                stroke={MEMORY_TYPE_COLORS.tool_cache} fill={MEMORY_TYPE_COLORS.tool_cache}
                fillOpacity={0.6} name="Tool Cache" />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {!loading && tokenTrendData.length === 0 && (
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No token usage data available</p>
              <p className="text-sm mt-1">Data will appear once agents start executing with memory operations</p>
            </div>
          </div>
        )}
      </div>

      {/* Agents at Risk Table */}
      {agentsAtRisk.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Runs Near Context Limits
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Agent runs using 70% or more of their available context window
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Run ID</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Memory Type</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Token Usage</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {agentsAtRisk.map((agent) => {
                  const { level } = getUtilizationLevel(agent.utilization, 100);
                  const colors = getUtilizationColorClasses(level);
                  return (
                    <tr key={agent.run_id}
                      className={`transition-colors ${agent.run_id ? 'hover:bg-gray-100 dark:hover:bg-gray-800/50 cursor-pointer' : ''}`}
                      onClick={() => agent.run_id && onNavigateToRun(agent.run_id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') agent.run_id && onNavigateToRun(agent.run_id); }} role="row" tabIndex={0}
                    >
                      <td className="py-3 px-4">
                        {agent.run_id ? (
                          <span className="text-sm font-mono text-blue-600 dark:text-blue-400 hover:underline">
                            {agent.run_id.slice(0, 8)}...
                          </span>
                        ) : (
                          <span className="text-sm font-mono text-gray-400 dark:text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${MEMORY_TYPE_COLORS[agent.memory_type ?? ''] ?? MEMORY_TYPE_COLORS.other}20`,
                            color: MEMORY_TYPE_COLORS[agent.memory_type ?? ''] ?? MEMORY_TYPE_COLORS.other,
                          }}
                        >
                          {agent.memory_type ?? 'unknown'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                        {formatNumber(agent.token_count)} / {formatNumber(agent.max_tokens)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mr-2">
                            <div className={`h-full ${colors.bg} transition-all`}
                              style={{ width: `${Math.min(agent.utilization, 100)}%` }} />
                          </div>
                          <span className={`text-sm font-medium ${colors.text}`}>
                            {agent.utilization.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryOverviewTab;
